import { beforeSendReportingEvent } from '@/core/reporting/privacy'
import { installReportingClient } from '@/core/reporting/runtime'
import type { ReportingConfiguration } from '@/core/reporting/types'

export const MISSING_SENTRY_DSN_MESSAGE = 'VITE_SENTRY_DSN is not set; see docs/MILESTONE-10-OBSERVABILITY.md'
const DISABLED_REPORTING_MESSAGE = '[reporting] disabled (no DSN)'
const FILTERED_BREADCRUMB_CATEGORIES = new Set(['fetch', 'xhr'])

let initialization: Promise<void> | null = null

export function validateReportingConfiguration(configuration: ReportingConfiguration): string | null {
  const dsn = configuration.dsn?.trim() || null
  if (!dsn && configuration.mode === 'production') throw new Error(MISSING_SENTRY_DSN_MESSAGE)
  return dsn
}

export function initErrorReporting(configuration: ReportingConfiguration = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  mode: import.meta.env.MODE,
}): Promise<void> {
  initialization ??= initializeErrorReporting(configuration)
  return initialization
}

async function initializeErrorReporting(configuration: ReportingConfiguration): Promise<void> {
  const dsn = validateReportingConfiguration(configuration)
  if (!dsn) {
    console.info(DISABLED_REPORTING_MESSAGE)
    return
  }
  const loadedSentry = await import('@sentry/react')
  loadedSentry.init({
    dsn,
    release: `${__APP_VERSION__}+${__APP_COMMIT__}`,
    environment: configuration.mode,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    enableLogs: true,
    maxBreadcrumbs: 30,
    integrations: [
      loadedSentry.browserApiErrorsIntegration(),
      loadedSentry.globalHandlersIntegration(),
    ],
    beforeSend: (event) => beforeSendReportingEvent(event, configuration.mode === 'production'),
    beforeBreadcrumb: (breadcrumb) => breadcrumb.category && FILTERED_BREADCRUMB_CATEGORIES.has(breadcrumb.category)
      ? null
      : breadcrumb,
  })
  installReportingClient(loadedSentry)
}
